import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Chip, MenuItem, Select } from '@mui/material';
import {
  addPlayerToTeam,
  delFanaticPosition,
  delPauseId,
  delTeamsRound,
  fanaticModeBefore,
  fanaticPlayer,
  selectDraftConfig,
  setCountRender,
  setDraftPlayersAction,
  setStatus,
  setTradeValue,
  uniqPosition,
} from '../../app/features/draftConfig/draftConfigSlice';
import {
  delPlayersDraft,
  playerPositionMulti,
  setPlayerManualChoose,
  setPositionPlayersDraft,
  setSearchPlayers,
  updateTeamADP,
  recordDraftPick,
  refreshTeamBoard,
  sortPlayersByTeam,
} from '../../app/features/playersDraft/playersDraftSlice';
import {
  manualTradeAction,
  selectTrades,
  setAcceptTrade,
  setChangeTrades,
  setRandomFlag,
  setResetSelectTeam,
} from '../../app/features/trades/tradesSlice';
import { POSITIONS_COLOR } from '../../utils/constants';
import { PLAYER_MAX } from '../../config/config';
import { boardAvailabilityRank, calculateADP, isOffUserBoard, thirdRoundReplayBlocks } from '../../utils/utils';
import {
  SidePanel,
  YourPickBody,
  PanelHeader,
  ClockBanner,
  ClockBannerLeft,
  ClockBannerPick,
  SheetToggleBtn,
  TeamNeedsRow,
  TeamNeedsTeam,
  TeamNeedsName,
  NeedsCard,
  NeedsCardLabel,
  NeedsCardValue,
  PosFilterRow,
  PosToggle,
  PosSelectWrap,
  PosOption,
  PosSelectValue,
  SearchWrap,
  SearchInput,
  TableHeader,
  TableHeaderPlayer,
  TableHeaderCell,
  PlayerRow,
  PlayerInfo,
  PlayerName,
  PlayerSchool,
  PositionBadge,
  PlayerRank,
  PlayerAdp,
  DraftPickBtn,
  PausedBanner,
  ResumeDraftBtn,
  EmptyState,
  PlayerListScroll,
  PicksRemaining,
  PicksRemainingSpinner,
  PicksRemainingCount,
  PicksRemainingLabel,
  PanelTabs,
  PanelTab,
  DraftTabArea,
  DraftTabStripes,
  StartBarText,
  StartBarBtn,
  SheetStartBar,
  SheetStartBtn,
} from './YourPickModal.styles';


const TurnClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" />
    <path d="M8 4.5V8l2.5 1.5" strokeLinecap="round" />
  </svg>
);

const TurnDotIcon = () => (
  <svg width="16" height="16" viewBox="-2 -2 14 14" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="5" r="5" />
  </svg>
);

// "You have no more picks" is a finished state, not a waiting one — it shared the clock with
// "waiting for your turn", which reads as though another turn is still coming.
const TurnDoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" />
    <path d="M5.2 8.1l2 2 3.6-4.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlayIcon = () => (
  <svg width="12" height="14" viewBox="0 0 10 12" fill="currentColor" aria-hidden="true">
    <path d="M0 0 L10 6 L0 12 Z" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7.41 15.41 12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
  </svg>
);

/* Mobile position dropdown: the same border, radius and focus colour as the search box above. */
const posSelectSx = {
  width: '100%',
  borderRadius: '10px',
  background: '#fff',
  '& .MuiSelect-select': { display: 'flex', alignItems: 'center', minHeight: 0, padding: '7px 36px 7px 10px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cad2da', borderWidth: '1.5px' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cad2da' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#004ea3', borderWidth: '1.5px' },
};

/* A position as a chip in its own colour — the same colours as the badges in the player list.
   Used in the closed dropdown and in its menu. One fixed width, sized for the widest label
   (iOL_C), so "S" and "EDGE" line up the same as the player-list badges do. */
const PosChip = ({ pos }) => (
  <Chip
    size="small"
    label={pos}
    sx={{
      width: '46px',
      bgcolor: POSITIONS_COLOR[pos] || '#e2e8f0',
      color: '#1a202c',
      fontFamily: "'Saira Semi Condensed', sans-serif",
      fontSize: '13px',
      fontWeight: 700,
      '& .MuiChip-label': { padding: 0, width: '100%', textAlign: 'center' },
    }}
  />
);

const posMenuProps = {
  PaperProps: { sx: { maxHeight: '55vh', borderRadius: '12px', border: '1.5px solid #cad2da' } },
};

/* Top of the board gets a light grey wash to set it apart from the long tail */
const SHADED_ROW_COUNT = 8;


// Draft a Player is always there; the Trade tab only when trading is available (a plain mock
// draft, not the challenges or fanatic mode). DraftBoard owns the trade (opening it pauses the
// draft) and passes it in as `tradePanel`.
//
// `sheet` is set on mobile only, where DraftBoard shows this panel as a bottom sheet:
// { open, onToggle, headerRef, swipeHandlers }. The header (banner, team, tabs) is what stays on
// screen while the sheet is closed; tapping the banner or team row toggles it, swiping it drags
// it. Without `sheet` the panel is the desktop one, unchanged.
const YourPickModal = ({
  playersDraft,
  draftStatus,
  setThisId,
  isDrafting = false,
  showTradeTab = false,
  tradeDisabledReason = null,
  activeTab = 'draft',
  onTabChange,
  tradePanel = null,
  onStartDraft,
  sheet = null,
}) => {
  const dispatch = useDispatch();
  const {
    tradeValue,
    countRender,
    status,
    teamPickIndex,
    fanaticIndexPosition,
    fanaticChallenge,
    fanaticMode,
    tradingSimulatorAction,
    round,
    teams,
    fanaticModeValue,
    teamSelect,
    teamUniqPosition,
    fanaticPlayerBefore,
    draftPlayers,
  } = useSelector(selectDraftConfig);
  const { randomAccepted, tradeRandomId } = useSelector(selectTrades);

  const [searchValue, setSearchValue] = useState('');
  // The scrollable player list, so a pick can send it back to the top.
  const listScrollRef = useRef(null);

  const currentIdx = status === 'red' ? countRender - 1 : countRender;
  const currentPick = tradeValue?.results?.[currentIdx];
  const roundNum = currentPick?.round_index_number;
  const pickNum = currentPick?.index;
  const teamName = currentPick?.round?.name;

  // The team board is shared, so another user can be drafting this same team while we're on the
  // clock — and a pick can sit for many minutes. Without this the board on screen would stay
  // frozen at whatever it was when the turn started, and the pick would be made on stale data.
  // Re-read the board when the turn begins, then poll slowly until it ends.
  const isMyTurn = !!teamName && teamSelect?.some((t) => t.name === teamName);
  // Two separate things: the draft being PAUSED (shows the resume banner), and simply not being
  // our turn (greys out the Draft buttons but shouldn't offer a resume).
  const isPaused = draftStatus === 'red';
  const draftBtnDisable = isPaused || !isMyTurn;
  useEffect(() => {
    if (!isMyTurn) return;
    // refreshTeamBoard only updates teamBoards/teamADP. The list on screen renders from
    // playersDraft.results, which sortPlayersByTeam builds — and that normally only re-runs when
    // the pick advances. So re-sort after each refresh, or the panel would keep showing the
    // board as it was when the turn started.
    const sync = async () => {
      await dispatch(refreshTeamBoard({ teamName }));
      dispatch(sortPlayersByTeam({ playerCountGet: PLAYER_MAX, teamName }));
    };
    sync();
    const id = setInterval(sync, 60000);
    return () => clearInterval(id);
  }, [dispatch, isMyTurn, teamName]);

  // The team this panel is about: whoever of ours is on the clock, and while we wait, the one
  // holding our next upcoming pick. Drives the header, the needs and which board the list shows,
  // so the user is always looking at the team they are about to draft for.
  // The user's next unfilled pick, scanning forward from the current slot. On their turn this is
  // the current pick itself; while waiting it is the one they are counting down to.
  const nextUserPick = useMemo(() => {
    const mine = new Set((teamSelect ?? []).map((t) => t?.name).filter(Boolean));
    const results = tradeValue?.results ?? [];
    for (let i = Math.max(currentIdx, 0); i < results.length; i++) {
      const row = results[i];
      if (!row?.player?.player && mine.has(row?.round?.name)) return row;
    }
    return null;
  }, [teamSelect, tradeValue?.results, currentIdx]);

  const activeTeamName = isMyTurn
    ? teamName
    : (nextUserPick?.round?.name ?? teamSelect?.[0]?.name ?? null);

  // Warm the board for the ONE team this panel is about — ours on the clock, or while we wait,
  // the one holding our next upcoming pick. During the simulation sortPlayersByTeam only
  // populates the board of the team currently picking, so ours may not be cached yet; without
  // this the panel would sit empty until the user's first turn. Previously this looped over
  // every selected team, fetching boards for teams that were not picking and would not pick next.
  useEffect(() => {
    if (!activeTeamName) return;
    const key = activeTeamName === '49ers' ? 'ers' : activeTeamName;
    if (playersDraft.teamBoards?.[key]?.length) return;
    dispatch(refreshTeamBoard({ teamName: activeTeamName }));
  }, [dispatch, activeTeamName, playersDraft.teamBoards]);

  // Has the active team's board arrived? Only one board is fetched at a time now, so switching to
  // a team whose board is not cached leaves the list empty for a moment — and "No players
  // available" is the wrong thing to say about a board that simply has not loaded.
  // Needs BOTH halves: the board supplies ids, allPlayers supplies the player objects those ids
  // map to. They are separate fetches and they race — when the board won, every lookup in
  // userBoardPlayers missed and the panel claimed "No players available" over a full board.
  const activeBoardLoaded = !!playersDraft.teamBoards?.[
    activeTeamName === '49ers' ? 'ers' : activeTeamName
  ]?.length && !!playersDraft.allPlayers?.length;

  // The one team the whole panel is about — header, needs and list all follow it.
  const panelTeam = teams?.find((t) => t.name === activeTeamName) ?? null;

  // Once the user is out of picks the board is unusable, so the panel swaps to the only number
  // still worth showing: how much of the mock draft is left to run.
  const outOfPicks = isDrafting && !nextUserPick;
  const picksRemaining = useMemo(
    () => (tradeValue?.results ?? []).filter((r) => !r?.player?.player).length,
    [tradeValue?.results]
  );

  // Real needs, straight from admin — every pick row in trade-value-history carries its owner's
  // team_neads_info, so no extra request. Priority groups (P1, P2, …) come back in order, so
  // flattening preserves it. Positions this team has already drafted are struck off as it picks.
  const teamNeeds = useMemo(() => {
    // Always the panel's team. Falling back to the current pick meant that once the user had no
    // unfilled picks left, the needs followed whichever simulated team was on the clock.
    const row = nextUserPick?.round?.name === activeTeamName
      ? nextUserPick
      : (tradeValue?.results ?? []).find((r) => r?.round?.name === activeTeamName);
    const groups = row?.team_neads_info?.team_neads_info ?? [];
    const drafted = new Set(panelTeam?.name ? (teamUniqPosition[panelTeam.name] ?? []) : []);
    const seen = new Set();
    return groups
      .flatMap((g) => (g?.positions ?? []).map((pos) => pos?.name).filter(Boolean))
      .filter((name) => !drafted.has(name) && !seen.has(name) && seen.add(name));
  }, [nextUserPick, activeTeamName, tradeValue?.results, panelTeam, teamUniqPosition]);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => dispatch(setSearchPlayers(searchValue)), 500);
    return () => clearTimeout(timer);
  }, [searchValue, dispatch]);

  // The panel always shows the USER's own board — not whichever team the simulator is currently
  // picking for. Derived here rather than from playersDraft.results, because that gets rebuilt
  // for each team as it comes on the clock (and the AI engine reads it, so it can't be changed).
  // Players drop off live as simulated teams take them, since draftPlayers lives in the store.
  const userBoardPlayers = useMemo(() => {
    // One team at a time — the active one. Merging every selected team's board produced a single
    // blended list, so on team B's clock the user could draft a player off team A's board.
    const myTeams = activeTeamName ? [activeTeamName] : [];
    if (!myTeams.length) return [];
    const allById = new Map((playersDraft.allPlayers ?? []).map((p) => [p.id, p]));

    const rows = myTeams.flatMap((name) => {
      const key = name === '49ers' ? 'ers' : name;
      const boardIds = playersDraft.teamBoards?.[key] ?? [];
      const adpMap = playersDraft.teamADP?.[key] ?? {};

      const gone = new Set(
        (draftPlayers ?? [])
          .filter((p) => isOffUserBoard(p, {
            iteration: currentPick?.iteration,
            teamName: name,
            mode: fanaticChallenge?.[0]?.mode,
          }))
          .map((p) => p.player.id)
      );

      // rank is the position on the FULL board, so the numbers keep their gaps as players are
      // taken (matching how the board reads elsewhere); adp is the team's own value.
      return boardIds
        .map((id, idx) => {
          const base = allById.get(id);
          if (!base || gone.has(id)) return null;
          const rank = idx + 1;
          return { ...base, myTeam: name, boardRank: rank, adp: adpMap[id] ?? rank };
        })
        .filter(Boolean);
    });

    return rows
      .sort((a, b) => a.adp - b.adp)
      .map((p, i) => ({ ...p, bpa: i + 1 }));
  }, [
    activeTeamName,
    playersDraft.teamBoards,
    playersDraft.teamADP,
    playersDraft.allPlayers,
    draftPlayers,
    currentPick?.iteration,
    fanaticChallenge,
  ]);

  // Position pills come from the user's own board too — only positions actually still available
  // there, so the filters match what the list can show.
  // Why a position pill is missing: it is absent only when no player of that position survives
  // into userBoardPlayers. Two very different reasons — every one of them is already drafted, or
  // their ids did not resolve against allPlayers. This says which.
  //
  // Prints only when something it reports can change: a new team or board, a change in how many
  // ids resolve, or a position appearing/disappearing. Keyed on the available COUNT it fired after
  // every pick — 23 of 93 lines in one run — since that number drops each time anyone drafts.
  const posLogRef = useRef(null);
  useEffect(() => {
    const key = activeTeamName === '49ers' ? 'ers' : activeTeamName;
    const boardIds = playersDraft.teamBoards?.[key] ?? [];
    if (!activeTeamName || !boardIds.length || !playersDraft.allPlayers?.length) return;

    const available = {};
    for (const p of userBoardPlayers) available[p.position] = (available[p.position] || 0) + 1;
    const allById = new Map(playersDraft.allPlayers.map((p) => [p.id, p]));
    const unresolved = boardIds.filter((id) => !allById.has(id));

    const sig = `${key}|${boardIds.length}|${unresolved.length}|${Object.keys(available).sort().join(',')}`;
    if (posLogRef.current === sig) return;
    posLogRef.current = sig;

    const onBoard = {};
    for (const id of boardIds) {
      const pos = allById.get(id)?.position;
      if (pos) onBoard[pos] = (onBoard[pos] || 0) + 1;
    }
    const drainedOut = Object.keys(onBoard).filter((pos) => !available[pos]);

    console.log(
      `[POSITIONS] ${key} | board ${boardIds.length}, resolved ${boardIds.length - unresolved.length}, ` +
      `available ${userBoardPlayers.length}` +
      (unresolved.length ? `  ⚠️ ${unresolved.length} board ids NOT in allPlayers` : '') +
      (drainedOut.length ? ` | all drafted: ${drainedOut.join(', ')}` : ''),
      '| on board:', onBoard, '| available:', available
    );
  }, [activeTeamName, playersDraft.teamBoards, playersDraft.allPlayers, userBoardPlayers]);

  const positionsList = useMemo(() => {
    const exclude = new Set(['All']);
    if ([1, 2, 3].includes(round)) { exclude.add('P'); exclude.add('K'); }
    const onBoard = new Set(userBoardPlayers.map((p) => p.position));
    return Object.keys(POSITIONS_COLOR).filter((pos) => !exclude.has(pos) && onBoard.has(pos));
  }, [round, userBoardPlayers]);

  // Filtered player list (no pagination)
  const filteredPlayers = useMemo(() => {
    let playersData = userBoardPlayers;
    const positions = playersDraft.position ?? ['All'];
    if (!positions.includes('All')) {
      playersData = playersData.filter((p) => positions.includes(p.position));
    }
    return playersData.filter((p) =>
      p.player.toLowerCase().includes((playersDraft.search ?? '').toLowerCase())
    );
  }, [playersDraft.search, playersDraft.position, userBoardPlayers]);

  // ── Core draft logic (mirrors DraftPlayerChoose) ─────────────────

  const playerConcat = (playerItem, teamId, upPlayers = [], idx, adpAtPick) => {
    const teamItem = structuredClone(tradeValue.results[teamId - 1]);
    teamItem['player'] = playerItem;
    teamItem['playerDepth'] = idx + 1;

    // BPA at the moment of the pick — see the matching stamp in DraftBoard.runAutoPick.
    const bpaBoardKey = teamItem.round.name === '49ers' ? 'ers' : teamItem.round.name;
    const bpaBoard = playersDraft.teamBoards?.[bpaBoardKey] ?? [];
    const pickedThisIter = new Set(
      draftPlayers
        .filter(p => p?.player?.id != null && p.iteration === teamItem.iteration)
        .map(p => p.player.id)
    );
    teamItem['boardBpaAtPick'] = boardAvailabilityRank(bpaBoard, pickedThisIter, playerItem.id);
    // Passed in from playerChoose, which computed it before calculateADP moved the value.
    teamItem['adpAtPick'] = adpAtPick;

    // gradeRank = the player's availability rank on the user's own board (players drafted this
    // iteration excluded). Same field the result page grades from for simulated picks.
    if (fanaticChallenge.length && fanaticChallenge[0].mode === 1) {
      const gradeBoard = bpaBoard;
      const gRank = teamItem['boardBpaAtPick'];
      const gFound = gRank != null;
      teamItem['gradeRank'] = gFound ? gRank : (playerItem.bpa ?? idx + 1);

      const nameById = new Map((playersDraft.allPlayers ?? []).map(p => [p.id, p.player]));
      console.log(
        `[GRADE-USER] iter ${teamItem.iteration} #${teamItem.index} ${teamItem.round.name} → ${playerItem.player}`,
        '| gradeRank:', teamItem['gradeRank'],
        '| board found:', gFound,
        '| available on board (top 12):', gradeBoard.filter(id => !pickedThisIter.has(id)).map(id => nameById.get(id) ?? id).slice(0, 12)
      );
    }
    // Second round challenge diagnostics for the USER's own picks — the [SRC] log in DraftBoard
    // only covers simulated teams. draftPlayers has not been written yet at this point, so the
    // counts below are strictly prior picks.
    if (fanaticChallenge.length && fanaticChallenge[0].mode === 2) {
      const myName = teamItem.round.name;
      const r = +teamItem.round_index_number;
      const iterN = +teamItem.iteration;
      const totalIter = fanaticChallenge[0].iteration ?? 10;
      const priorSame = draftPlayers.filter(
        (p) => p?.player?.id === playerItem.id && p.round?.name === myName
      );
      const priorR1 = priorSame.filter((p) => +p.round_index_number === 1).length;
      const priorR2 = priorSame.filter((p) => +p.round_index_number === 2).length;
      const forcedSlot = r === 2 && iterN < totalIter ? teamItem.index - (totalIter - iterN) : null;
      console.log(
        `[SRC-USER] iter ${iterN} (block ${Math.ceil(iterN / 5)}) r${r} pick ${teamItem.index} — ` +
        `${myName} selects ${playerItem.player}` +
        (forcedSlot ? ` | forces slot ${forcedSlot} from iter ${iterN + 1}` : '') +
        ` | previously taken by ${myName}: r1 ${priorR1}, r2 ${priorR2}` +
        (r === 2 && priorR2 > 0 ? '  ⚠️ ROUND 2 REPEAT — breaks uniqueness (rule 5)' : '') +
        (r === 1 && priorR2 > 0 ? '  ⚠️ ROUND 1 TOOK A PRIOR ROUND 2 PICK (rule 6)' : '')
      );
    }

    // Third round challenge: this round-3 pick's replay block, and the slot in it that this
    // iteration's player goes to (none in the last iteration). Laid out from all of the team's
    // picks every time — ownership can't change, trades are off in the challenges.
    let trcBlock = null;
    let trcSlot = null;
    if (fanaticChallenge.length && fanaticChallenge[0].mode === 3 && +teamItem.round_index_number === 3) {
      const totalIter = fanaticChallenge[0].iteration ?? 10;
      const mine = tradeValue.results.filter((item) => item.round?.name === teamItem.round.name);
      const blocks = thirdRoundReplayBlocks(
        mine.map((item) => +item.index),
        mine.filter((item) => +item.round_index_number === 3).map((item) => +item.index),
        totalIter - 1
      );
      trcBlock = blocks.get(+teamItem.index) ?? null;
      trcSlot = +teamItem.iteration < totalIter ? trcBlock?.[+teamItem.iteration - 1] ?? null : null;
    }

    // Third round challenge diagnostics for the USER's own picks — [TRC] in DraftBoard covers the
    // simulated teams. Prior counts are strictly earlier picks (draftPlayers isn't written yet).
    if (fanaticChallenge.length && fanaticChallenge[0].mode === 3) {
      const myName = teamItem.round.name;
      const r = +teamItem.round_index_number;
      const iterN = +teamItem.iteration;
      const totalIter = fanaticChallenge[0].iteration ?? 10;
      const priorSame = draftPlayers.filter(
        (p) => p?.player?.id === playerItem.id && p.round?.name === myName
      );
      const priorBy = (round) => priorSame.filter((p) => +p.round_index_number === round).length;
      const priorR3 = priorBy(3);
      // "57-65", or the slots listed when the count skipped one of the team's own picks.
      const slots = (trcBlock ?? []).filter((n) => n != null);
      const contiguous = slots.every((n, i) => i === 0 || n === slots[i - 1] + 1);
      const blockText = !slots.length ? 'none'
        : contiguous ? `${slots[0]}-${slots[slots.length - 1]}` : slots.join(',');
      console.log(
        `[TRC-USER] iter ${iterN} (block ${Math.ceil(iterN / 5)}) r${r} pick ${teamItem.index} — ` +
        `${myName} selects ${playerItem.player}` +
        (r === 3 ? ` | replay block ${blockText}` : '') +
        (trcSlot != null ? ` → forces slot ${trcSlot} from iter ${iterN + 1}` : '') +
        ` | previously taken by ${myName}: r1 ${priorBy(1)}, r2 ${priorBy(2)}, r3 ${priorR3}` +
        (r === 3 && priorR3 > 0 ? '  ⚠️ ROUND 3 REPEAT — breaks uniqueness' : '') +
        (r < 3 && priorR3 > 0 ? `  ⚠️ ROUND ${r} TOOK A PRIOR ROUND 3 PICK` : '') +
        (r === 3 && iterN < totalIter && trcSlot == null ? '  ⚠️ NO REPLAY SLOT — the count ran past pick 1' : '')
      );
    }

    const newTradeValue = tradeValue.results.map((item) => {
      if (item.index === teamItem.index && item?.index_position === teamItem?.index_position) {
        return teamItem;
      }
      return item;
    });

    if (fanaticChallenge.length) {
      if (fanaticChallenge[0].mode === +teamItem.round_index_number) {
        let pickBefore;
        if (fanaticChallenge[0].mode === 1) {
          // picks 1-5 (pick < 6): no forced pre-pick — handled by the picks 1-5 /
          // unique-user-selection rule, so leave pickBefore undefined for those.
          if (teamItem['pick'] >= 6) {
            const totalIter = fanaticChallenge[0].iteration;      // 5 for the first round challenge
            const currentIter = +teamItem.iteration;
            const pickIdx = +teamItem['pick'];
            const blockSize = totalIter - 1;                      // n-1 logic → 4 forced slots per pick

            // All unique round-1 pick slots owned by the user's team, ascending.
            const uniqueUserPicks = [...new Set(
              tradeValue.results
                .filter(item => item.round.name === teamSelect[0].name && +item.round_index_number === 1)
                .map(item => +item.pick)
            )].sort((a, b) => a - b);

            // Pair up consecutive user picks that sit within `totalIter` slots of each other.
            // For such a nearby pair, the forced slots are aggregated into the 2*blockSize
            // positions immediately before the EARLIER pick. Per the client-confirmed mapping
            // (Jets #26 & #29): the LATER pick takes the first block (#18–#21) and the EARLIER
            // pick takes the second block (#22–#25); the gap picks (#27, #28) stay normal.
            const earlierPartner = new Map(); // laterPick  -> its earlier partner
            const laterPartner = new Map();   // earlierPick -> its later partner
            for (let i = 1; i < uniqueUserPicks.length; i++) {
              const diff = uniqueUserPicks[i] - uniqueUserPicks[i - 1];
              if (diff > 0 && diff < totalIter) {
                earlierPartner.set(uniqueUserPicks[i], uniqueUserPicks[i - 1]);
                laterPartner.set(uniqueUserPicks[i - 1], uniqueUserPicks[i]);
              }
            }

            // The final iteration has no following iteration to force into.
            if (currentIter < totalIter) {
              let forcedSlot;
              if (laterPartner.has(pickIdx)) {
                // EARLIER pick of a nearby pair → second block: [pickIdx-blockSize .. pickIdx-1]
                forcedSlot = pickIdx - blockSize + (currentIter - 1);
              } else if (earlierPartner.has(pickIdx)) {
                // LATER pick of a nearby pair → first block, ahead of the earlier pick's block:
                // [E-2*blockSize .. E-blockSize-1], where E is the earlier partner's slot.
                const earlierPick = earlierPartner.get(pickIdx);
                forcedSlot = earlierPick - 2 * blockSize + (currentIter - 1);
              } else {
                // Single (non-nearby) pick → standard formula: index - (totalIter - iteration)
                forcedSlot = teamItem['index'] - (totalIter - currentIter);
              }

              dispatch(fanaticPlayer({
                pick: forcedSlot,
                player: playerItem,
                playerDepth: idx + 3,
                round: +teamItem.round_index_number,
              }));
            }
          }
        } else if (fanaticChallenge[0].mode === 2) {
          // Second round challenge: each iteration's pick is forced into one of the 9 slots
          // immediately before the user's slot — iteration 1 → index-9 … iteration 9 → index-1.
          // The last iteration has no following iteration to force into; 10 - 10 = 0 used to
          // write the forced pick onto the user's OWN slot.
          const totalIter = fanaticChallenge[0].iteration ?? 10;
          if (+teamItem.iteration < totalIter) {
            pickBefore = totalIter - +teamItem.iteration;
          }
        } else if (fanaticChallenge[0].mode === 3) {
          // Third round challenge: the slot comes from the grouped layout above (trcSlot), not a
          // fixed distance — close round-3 picks share a stacked window. v1's `15 - iteration` stub
          // (15 iterations, no grouping, wrote onto the user's own slot at the end) is gone.
          if (trcSlot != null) {
            dispatch(
              fanaticPlayer({
                pick: trcSlot,
                player: playerItem,
                playerDepth: idx + 3,
                round: +teamItem.round_index_number,
              })
            );
          }
        }
        if (pickBefore !== undefined) {
          dispatch(
            fanaticPlayer({
              pick: teamItem['index'] - pickBefore,
              player: playerItem,
              playerDepth: idx + 3,
              round: +teamItem.round_index_number,
            })
          );
        }
      }
    }

    if (fanaticMode) {
      const findNextItem = tradeValue.results.filter(
        (item) => item.round.name === teamSelect[0].name
      );
      const indicesToCalculate = new Set();
      const indicesToNotCalculate = new Set();
      for (let i = 1; i < findNextItem.length; i++) {
        const diff = findNextItem[i].index - findNextItem[i - 1].index;
        if (diff < fanaticModeValue && diff > 0) {
          indicesToCalculate.add(findNextItem[i - 1].index);
          indicesToNotCalculate.add(findNextItem[i].index);
        }
      }
      const uniqueCalc = Array.from(indicesToCalculate);
      const uniqueNotCalc = Array.from(indicesToNotCalculate);
      let forcedSlot;
      let slotRule;
      if (indicesToCalculate.size !== 0 && uniqueCalc.includes(teamItem['index'])) {
        const pickBefore = 2 * (fanaticModeValue - +teamItem.iteration);
        forcedSlot = teamItem['index'] - pickBefore;
        slotRule = `nearby-earlier, 2*(${fanaticModeValue}-${teamItem.iteration})=${pickBefore} before`;
      } else if (indicesToCalculate.size !== 0 && uniqueNotCalc.includes(teamItem['index'])) {
        const pickBefore = 2 * (fanaticModeValue - +teamItem.iteration);
        forcedSlot = uniqueCalc[0] - pickBefore + 1;
        slotRule = `nearby-later, off partner ${uniqueCalc[0]}`;
      } else {
        const pickBefore = fanaticModeValue - +teamItem.iteration;
        forcedSlot = teamItem['index'] - pickBefore;
        slotRule = `single, ${fanaticModeValue}-${teamItem.iteration}=${pickBefore} before`;
      }
      // The last iteration has no following iteration to force into, and every formula above
      // reduces to "0 picks before" there — which registered the slot onto the user's OWN pick.
      // Same bug the second round challenge had at iteration 10.
      const isLastIteration = +teamItem.iteration >= fanaticModeValue;
      if (!isLastIteration) {
        dispatch(
          fanaticModeBefore({
            player: { pick: forcedSlot, player: playerItem, playerDepth: idx + 1, round: +teamItem.round_index_number },
            action: 'inc',
          })
        );
      }

      // Fanatic mode has no diagnostics of its own — [FRC] is mode 1, [SRC] is mode 2. Two things
      // left to watch: a slot out of range, and a slot already claimed by an earlier entry
      // (fanaticModeBefore appends rather than replacing, unlike fanaticPlayer).
      const already = (fanaticPlayerBefore ?? []).filter((p) => p?.pick === forcedSlot);
      console.log(
        `[FAN-USER] iter ${teamItem.iteration} r${teamItem.round_index_number} pick ${teamItem.index} — ` +
        `${teamItem.round.name} selects ${playerItem.player} | ` +
        (isLastIteration
          ? 'last iteration, no slot registered'
          : `forces slot ${forcedSlot} (${slotRule})`) +
        (!isLastIteration && forcedSlot === teamItem['index'] ? '  ⚠️ SLOT IS THE USER\'S OWN PICK' : '') +
        (!isLastIteration && forcedSlot < 1 ? '  ⚠️ SLOT OUT OF RANGE' : '') +
        (!isLastIteration && already.length
          ? `  ⚠️ SLOT ALREADY HELD BY ${already.map((p) => p.player?.player).join(', ')}` : '')
      );
    }

    // Plain mock draft, user side — the counterpart to [PLAIN] in DraftBoard, which only covers
    // simulated teams. Same single invariant: the player must not already be off the board. With
    // several teams selected it also names which of them this pick belongs to, since the panel
    // follows whichever is on the clock.
    if (!fanaticChallenge.length && !fanaticMode) {
      const prior = draftPlayers.filter((p) => p?.player?.id === playerItem.id);
      const mine = (teamSelect ?? []).map((t) => t?.name).filter(Boolean);
      console.log(
        `[PLAIN-USER] r${teamItem.round_index_number} pick ${teamItem.index} — ` +
        `${teamItem.round.name} selects ${playerItem.player} (${playerItem.position}) | ` +
        `bpa ${teamItem['boardBpaAtPick'] ?? '—'} | adp ${teamItem['adpAtPick'] ?? '—'}` +
        (mine.length > 1 ? ` | user teams: ${mine.join(', ')}` : '') +
        (prior.length
          ? `  ⚠️ ALREADY DRAFTED by ${prior
              .map((p) => `${p.round?.name} r${p.round_index_number} pick ${p.index}`)
              .join('; ')}`
          : '') +
        (!mine.includes(teamItem.round.name)
          ? '  ⚠️ PICK IS NOT FOR ONE OF THE USER\'S TEAMS'
          : '')
      );
    }

    const playerItemPos = { ...playerItem, roundTeam: +teamItem.round_index_number };
    dispatch(delPlayersDraft([playerItemPos]));
    dispatch(setTradeValue({ ...tradeValue, results: newTradeValue }));
    dispatch(setDraftPlayersAction({ ...teamItem, upPlayers }));
    // Record the position against this team. runAutoPick does this for simulated picks; the
    // user's own were never registered, so a need they had just drafted stayed on the list.
    dispatch(uniqPosition({ name: teamItem.round.name, position: playerItem.position }));

    if (fanaticChallenge.length !== 0) {
      // Recorded for every round — see the matching note in DraftBoard.runAutoPick.
      dispatch(
        addPlayerToTeam({
          teamId: teamItem.round.id,
          playerData: { name: playerItem.player, id: playerItem.id },
          infoDraft: { round: +teamItem.round_index_number, iteration: teamItem.iteration, pick: teamItem.pick },
        })
      );
      // The old wipe of choosePlayers / infoDraft at the end of iterations 5 and 10 is gone.
      // It only fired when the user's team happened to own the last pick of round 1, and the
      // 2-per-5-iterations reset is now handled by the block count in filterPlayers. Wiping the
      // record here would also clear the user team's "never re-pick a player" history.
    }
  };

  const playerChoose = (item, bpa) => {
    const team = teamPickIndex[0] ?? fanaticIndexPosition[0];
    const teamItem = structuredClone(tradeValue.results[team - 1]);
    // Selection number is the team's draft slot, held CONSTANT across the 5 iterations
    // (client confirmed: no iteration reduction, the 1/8 factor is out).
    const selection = teamItem.index;

    const teamNameLocal = tradeValue.results[team - 1].round.name;
    const teamNameKey = teamNameLocal === '49ers' ? 'ers' : teamNameLocal;
    const teamADPMap = playersDraft.teamADP?.[teamNameKey] ?? {};
    // ADP runs over the AVAILABLE pool only — players already drafted this iteration are off
    // the board and must not be counted.
    // A player is unavailable to THIS pick — and so must not be shifted by the move-down — under
    // the same rule the panel list uses; see isOffUserBoard.
    const userTeamName = teamItem.round.name;
    const unavailableIds = new Set(
      draftPlayers
        .filter(p => isOffUserBoard(p, {
          iteration: teamItem.iteration,
          teamName: userTeamName,
          mode: fanaticChallenge?.[0]?.mode,
        }))
        .map(p => p.player.id)
    );
    const adpBoardIds = playersDraft.teamBoards?.[teamNameKey] ?? [];
    const teamBoardRankForADP = new Map(adpBoardIds.map((id, idx) => [id, idx + 1]));
    const baseAdp = (p) => teamADPMap[p.id] ?? teamBoardRankForADP.get(p.id) ?? p.ranking ?? 0;
    const allByIdADP = new Map((playersDraft.allPlayers ?? []).map(p => [p.id, p]));
    const availableBoardADP = (adpBoardIds.length
      ? adpBoardIds.map(id => allByIdADP.get(id)).filter(Boolean)
      : playersDraft.results
    ).filter(p => !unavailableIds.has(p.id));
    const boardWithADP = availableBoardADP.map(p => ({ ...p, adp: baseAdp(p) }));
    const pickedWithADP = { ...item, adp: baseAdp(item) };

    // Record the pick with the backend as it happens — the server owns the ADP math and applies
    // it atomically, so concurrent drafts on this team accumulate instead of overwriting. Send
    // no ADP values; drafted_player_ids is everything already off the board, minus this pick.
    dispatch(recordDraftPick({
      teamName: teamNameLocal,
      playerId: item.id,
      selection,
      draftedPlayerIds: [...unavailableIds].filter(id => id !== item.id),
    }));

    const adpMap = calculateADP(selection, boardWithADP, pickedWithADP);
    // [ADP] running-average trace — picked before→after (or "value, no move"), plus every
    // skipped player pulled toward the selection number. Watch the flip and the collapse here.
    {
      const r2 = (n) => Math.round(n * 100) / 100;
      const beforeADP = new Map(boardWithADP.map(p => [p.id, p.adp]));
      const pickedAfter = adpMap[item.id];
      const skipped = Object.entries(adpMap)
        .filter(([id]) => +id !== item.id)
        .sort((a, b) => beforeADP.get(+a[0]) - beforeADP.get(+b[0]))
        .slice(0, 8)
        .map(([id, after]) => `${allByIdADP.get(+id)?.player ?? id} ${r2(beforeADP.get(+id))}→${r2(after)}`);
      console.log(
        `[ADP] sel ${selection} it${teamItem.iteration} ${teamNameKey} picked ${item.player} ` +
        `${r2(pickedWithADP.adp)}${pickedAfter != null ? '→' + r2(pickedAfter) : ' (value, no move)'}` +
        (skipped.length ? ` | skipped↓: ${skipped.join(', ')}` : '')
      );
    }
    if (Object.keys(adpMap).length) {
      dispatch(updateTeamADP({ teamName: teamNameKey, adpMap }));
    }

    const upPlayersList = Object.entries(adpMap).map(([id, pricentValue]) => ({ id: +id, pricentValue }));

    const playerItemPos = { ...item, roundTeam: +teamItem.round_index_number };

    // Filters go back to default for the next pick — the position pills and the search box
    // both, so a name search doesn't survive into the following turn. The list scrolls back to
    // the top with them: after picking from deep in the board, the next turn would otherwise
    // open part-way down a board that has since re-sorted.
    dispatch(setPositionPlayersDraft(['All']));
    setSearchValue('');
    dispatch(setSearchPlayers(''));
    if (listScrollRef.current) listScrollRef.current.scrollTop = 0;
    playerConcat(item, team, upPlayersList, bpa, pickedWithADP.adp);
    dispatch(setPlayerManualChoose(playerItemPos));
    dispatch(delTeamsRound(teamPickIndex[0]));
    dispatch(delFanaticPosition(fanaticIndexPosition[0]));
    setThisId(teamPickIndex[0]);
    dispatch(setCountRender());
  };

  const tradeAction = useCallback(() => {
    dispatch(setChangeTrades(false));
    dispatch(manualTradeAction({ countRender, manualTrade: true }));
    dispatch(setResetSelectTeam());
    dispatch(setAcceptTrade(false));
  }, [countRender, dispatch]);

  useEffect(() => {
    // Never while the Trade tab is open. This resets the trade (teams, "Trade Accepted") and
    // re-runs whenever tradeRandomId changes — which a trade causes, as DraftBoard drops the picks
    // traded away — so a trade made on the clock at one of these picks was wiped straight after.
    if (activeTab === 'trade') return;
    if (randomAccepted && tradeRandomId.length > 0 && tradeRandomId.includes(countRender + 1)) {
      tradeAction();
      dispatch(setRandomFlag(true));
    }
  }, [randomAccepted, tradeRandomId, countRender, tradeAction, dispatch, activeTab]);

  // ── Derived ──────────────────────────────────────────────────────

  const selectedPositions = playersDraft.position ?? ['All'];
  const onClock = isDrafting && isMyTurn;
  // Mobile dropdown: the positions ticked, or none for All. Each change goes through
  // playerPositionMulti, the same as tapping a pill, so its rules carry over (iOL brings iOL_C,
  // choosing All clears the rest, an empty selection falls back to All).
  const pickedPositions = selectedPositions.filter((pos) => pos !== 'All');
  const handlePosSelect = (event) => {
    const next = event.target.value;
    const toggled =
      next.find((pos) => !pickedPositions.includes(pos)) ??
      pickedPositions.find((pos) => !next.includes(pos));
    if (toggled) dispatch(playerPositionMulti(toggled));
  };
  // Mobile, before the start, sheet closed: Start Draft replaces the team row and tabs, so the
  // way in is right there. Opening the sheet brings them back — a trade can still be made first.
  const showSheetStart = !!sheet && !sheet.open && !isDrafting && !!onStartDraft;

  return (
    <SidePanel>
      <YourPickBody>
          <PanelHeader ref={sheet?.headerRef} {...sheet?.swipeHandlers}>
          {/* Turn banner. Before the start it says so — never "on the clock", even when the user
              holds pick 1, since nothing can be drafted until Start Draft. */}
          <ClockBanner $active={onClock} role="status" aria-live="polite" onClick={sheet?.onToggle}>
            <ClockBannerLeft>
              {onClock ? <TurnDotIcon />
                : isDrafting && !nextUserPick ? <TurnDoneIcon />
                : <TurnClockIcon />}
              {!isDrafting ? 'Draft not started'
                : onClock ? "You're on the clock!"
                : !nextUserPick ? 'You have no more picks'
                : 'Waiting for your turn'}
            </ClockBannerLeft>
            {/* On the clock this is the user's own pick, so it stands alone. While waiting, the
                current pick belongs to someone else — show what they are waiting FOR instead.
                Shorter on mobile, where it shares the banner with the sheet toggle. */}
            {onClock
              ? pickNum != null && (
                  <ClockBannerPick>
                    {sheet ? `R${roundNum} · Pick ${pickNum}` : `Round ${roundNum}, Pick ${pickNum}`}
                  </ClockBannerPick>
                )
              : nextUserPick && (
                  <ClockBannerPick>
                    {sheet
                      ? `Next: R${nextUserPick.round_index_number} · Pick ${nextUserPick.index}`
                      : `Your next pick: Round ${nextUserPick.round_index_number} · Pick ${nextUserPick.index}`}
                  </ClockBannerPick>
                )}
            {/* No handler of its own: the click bubbles to the banner, which toggles. The button
                is there for keyboard and screen-reader users. */}
            {sheet && (
              <SheetToggleBtn
                type="button"
                $open={sheet.open}
                aria-expanded={sheet.open}
                aria-label={sheet.open ? 'Hide player list' : 'Show player list'}
              >
                <ChevronUpIcon />
              </SheetToggleBtn>
            )}
          </ClockBanner>

          {/* Names the team whose board is below — ours on the clock, else the one holding our
              next pick. Needs show throughout: they come from admin and belong to that team, so
              they are just as useful while waiting as they are on the clock. */}
          {showSheetStart && (
            <SheetStartBar>
              <SheetStartBtn type="button" onClick={onStartDraft}>
                <PlayIcon />
                Start Draft
              </SheetStartBtn>
            </SheetStartBar>
          )}

          {!outOfPicks && !showSheetStart && (
          <TeamNeedsRow onClick={sheet?.onToggle}>
            <TeamNeedsTeam>
              {panelTeam?.logo && <img src={panelTeam.logo} alt="" />}
              <TeamNeedsName>{panelTeam?.name || '—'}</TeamNeedsName>
            </TeamNeedsTeam>
            {teamNeeds.length > 0 && (
              <NeedsCard>
                <NeedsCardLabel>Needs</NeedsCardLabel>
                <NeedsCardValue>{teamNeeds.join(', ')}</NeedsCardValue>
              </NeedsCard>
            )}
          </TeamNeedsRow>
          )}

          {!outOfPicks && !showSheetStart && (
          <PanelTabs role="tablist">
            <PanelTab
              type="button"
              role="tab"
              aria-selected={activeTab === 'draft'}
              $active={activeTab === 'draft'}
              onClick={() => onTabChange?.('draft')}
            >
              Draft a Player
            </PanelTab>
            {showTradeTab && (
              <PanelTab
                type="button"
                role="tab"
                aria-selected={activeTab === 'trade'}
                $active={activeTab === 'trade'}
                disabled={!!tradeDisabledReason}
                title={tradeDisabledReason || undefined}
                onClick={() => onTabChange?.('trade')}
              >
                Trade
              </PanelTab>
            )}
          </PanelTabs>
          )}
          </PanelHeader>

          {showTradeTab && activeTab === 'trade' ? tradePanel : (
            <>
              <DraftTabArea>
                {outOfPicks ? (
                  <PicksRemaining role="status">
                    <PicksRemainingSpinner />
                    <PicksRemainingCount>{picksRemaining}</PicksRemainingCount>
                    <PicksRemainingLabel>
                      {picksRemaining === 1 ? 'Pick remaining' : 'Picks remaining'} in mock draft
                    </PicksRemainingLabel>
                  </PicksRemaining>
                ) : (
                <>
                {/* Search */}
                <SearchWrap>
                  <SearchInput
                    type="text"
                    placeholder="Search player..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                </SearchWrap>

                {/* Position filter: a multi-select dropdown on mobile, the pill row otherwise */}
                {sheet ? (
                <PosSelectWrap>
                  <Select
                    multiple
                    displayEmpty
                    size="small"
                    value={pickedPositions}
                    onChange={handlePosSelect}
                    sx={posSelectSx}
                    MenuProps={posMenuProps}
                    inputProps={{ 'aria-label': 'Filter by position' }}
                    // Every chosen position as a chip; they wrap onto more lines as needed.
                    renderValue={(picked) => (picked.length === 0 ? (
                      <PosOption>All positions</PosOption>
                    ) : (
                      <PosSelectValue>
                        {picked.map((pos) => <PosChip key={pos} pos={pos} />)}
                      </PosSelectValue>
                    ))}
                  >
                    <MenuItem value="All">
                      <Checkbox size="small" checked={pickedPositions.length === 0} sx={{ p: '4px', mr: 1 }} />
                      <PosOption>All positions</PosOption>
                    </MenuItem>
                    {positionsList.map((pos) => (
                      <MenuItem key={pos} value={pos}>
                        <Checkbox size="small" checked={pickedPositions.includes(pos)} sx={{ p: '4px', mr: 1 }} />
                        <PosChip pos={pos} />
                      </MenuItem>
                    ))}
                  </Select>
                </PosSelectWrap>
                ) : (
                <PosFilterRow>
                  <PosToggle
                    $active={selectedPositions.includes('All')}
                    $primary
                    onClick={() => dispatch(setPositionPlayersDraft(['All']))}
                  >
                    All
                  </PosToggle>
                  {positionsList.map((pos) => (
                    <PosToggle
                      key={pos}
                      $active={selectedPositions.includes(pos)}
                      $color={POSITIONS_COLOR[pos]}
                      onClick={() => dispatch(playerPositionMulti(pos))}
                    >
                      {pos}
                    </PosToggle>
                  ))}
                </PosFilterRow>
                )}

                {/* Table header */}
                <TableHeader>
                  <TableHeaderPlayer>Player</TableHeaderPlayer>
                  <TableHeaderCell>Position</TableHeaderCell>
                  <TableHeaderCell>BPA</TableHeaderCell>
                  <TableHeaderCell>ADP</TableHeaderCell>
                  {/* empty cell keeps the header aligned over the Draft button */}
                  <span />
                </TableHeader>

                {/* Player rows */}
                <PlayerListScroll ref={listScrollRef}>
                  {/* "No players available" is only ever said about a board that HAS loaded — only one
                      board is fetched at a time, so switching teams leaves a brief gap. */}
                  {!activeBoardLoaded ? (
                    <EmptyState>Loading {activeTeamName || 'team'} board…</EmptyState>
                  ) : filteredPlayers.length === 0 ? (
                    <EmptyState>No players available</EmptyState>
                  ) : (
                    filteredPlayers.map((item, idx) => (
                      <PlayerRow key={idx} $shaded={idx < SHADED_ROW_COUNT}>
                        <PlayerInfo>
                          <PlayerName>{item.player}</PlayerName>
                          <PlayerSchool>{item.school}</PlayerSchool>
                        </PlayerInfo>
                        <PositionBadge $color={POSITIONS_COLOR[item.position]}>
                          {item.position}
                        </PositionBadge>
                        {/* BPA — rank among the players still available, so it closes up as the board
                            empties. Matches the team board exactly when nothing has been drafted. */}
                        <PlayerRank>#{item.bpa ?? item.boardRank ?? item.ranking}</PlayerRank>
                        <PlayerAdp>{item.adp != null ? Math.round(item.adp * 1000) / 1000 : '—'}</PlayerAdp>
                        <DraftPickBtn
                          disabled={draftBtnDisable}
                          onClick={() => playerChoose(item, item?.bpa)}
                        >
                          Draft
                        </DraftPickBtn>
                      </PlayerRow>
                    ))
                  )}

                  {/* Resume button when paused */}
                  {isPaused && (
                    <PausedBanner>
                      <ResumeDraftBtn
                        onClick={() => {
                          dispatch(setStatus('green'));
                          dispatch(delPauseId());
                        }}
                      >
                        Start Draft
                      </ResumeDraftBtn>
                    </PausedBanner>
                  )}
                </PlayerListScroll>
                </>
                )}
                {/* Before the start: the overlay, with the way in at its centre */}
                {!isDrafting && (
                  <DraftTabStripes>
                    {onStartDraft && (
                      <>
                        <StartBarText>Start the draft to make picks</StartBarText>
                        <StartBarBtn type="button" onClick={onStartDraft}>
                          <PlayIcon />
                          Start
                        </StartBarBtn>
                      </>
                    )}
                  </DraftTabStripes>
                )}
              </DraftTabArea>
            </>
          )}
        </YourPickBody>
    </SidePanel>
  );
};

export default YourPickModal;
